import React, { useEffect } from "react";
import { Card, Tabs, Button, Row, Col, Space, Spin } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";

import { reducer, sliceKey, actions } from "./slice";
import { commodityComplianceSaga } from "./saga";
import {
  selectComplianceData,
  selectLoading,
  selectCategory,
  selectView,
} from "./selectors";

import "./CommodityCompliance.css";

const { TabPane } = Tabs;

// Color logic for percentage
const getColor = (value) => {
  if (value >= 85) return "#C46200"; // dark orange
  if (value >= 75) return "#f59e0b"; // amber
  return "#dc2626"; // red
};

// Matching background with transparency
const getBgColor = (value) => {
  if (value >= 85) return "rgba(196, 98, 0, 0.1)";
  if (value >= 75) return "rgba(245, 158, 11, 0.1)";
  return "rgba(220, 38, 38, 0.1)";
};

const CommodityCompliance = ({ filters = {}, onDetailedClick = () => {} }) => {
  const dispatch = useDispatch();

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: commodityComplianceSaga });

  const data = useSelector(selectComplianceData);
  const loading = useSelector(selectLoading);
  const activeTab = useSelector(selectCategory);
  const viewType = useSelector(selectView);

  const filterString = React.useMemo(() => {
    const filterObj = {
      companyId: filters.companyIds?.[0] || filters.companyId,
      ...filters,
    };
    return JSON.stringify(filterObj);
  }, [filters]);

  useEffect(() => {
    dispatch(
      actions.fetchCompliance({
        filtersString: filterString,
        category: activeTab,
        view: viewType,
      })
    );
  }, [dispatch, filterString, activeTab, viewType]);

  const handleTabChange = (key) => {
    dispatch(actions.setCategory(key));
  };

  const handleViewChange = (view) => {
    dispatch(actions.setView(view));
  };

  // Helper to get the right sample counts based on category
  const getSampleInfo = (item, category) => {
    switch (category) {
      case "Safety":
        return {
          label1: "Total Samples",
          value1: item.totalSafetySamples,
          label2: "Safe Samples",
          value2: item.safeSamples,
          percentage: item.overallCompliancePercentage,
        };
      case "Quality":
        return {
          label1: "Total Samples",
          value1: item.totalQualitySamples,
          label2: "Standard Samples",
          value2: item.qualitySamples,
          percentage: item.overallCompliancePercentage,
        };
      case "Overall":
      default:
        return {
          label1: "Total Samples",
          value1: item.totalSamples,
          label2: "Compliant Samples",
          value2: item.compliantSamples,
          percentage: item.overallCompliancePercentage,
        };
    }
  };

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <span style={{ color: "white", fontWeight: "600" }}>
              Commodity-wise Compliance
            </span>
          </Col>
          {/* <Col>
            <Button
              type="primary"
              onClick={onDetailedClick}
              style={{
                backgroundColor: "#1e2a38",
                border: "1px solid #fff",
                color: "#fff",
              }}
            >
              Detailed View
            </Button>
          </Col> */}
        </Row>
      }
      headStyle={{
        backgroundColor: "#1e2a38",
        borderBottom: "none",
      }}
      bodyStyle={{ backgroundColor: "#f9fafb" }}
    >
      {/* Tabs + Buttons Row */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            type="line"
            style={{ minWidth: 250 }}
          >
            <TabPane tab="Overall" key="Overall" />
            <TabPane tab="Safety" key="Safety" />
            <TabPane tab="Quality" key="Quality" />
          </Tabs>
        </Col>

        <Col>
          <Space>
            <Button
              shape="round"
              onClick={() => handleViewChange("top")}
              style={{
                backgroundColor: viewType === "top" ? "black" : "#f0f0f0",
                color: viewType === "top" ? "white" : "black",
                border: "none",
              }}
              icon={<ArrowUpOutlined />}
            >
              Top 10
            </Button>
            <Button
              shape="round"
              onClick={() => handleViewChange("bottom")}
              style={{
                backgroundColor: viewType === "bottom" ? "black" : "#f0f0f0",
                color: viewType === "bottom" ? "white" : "black",
                border: "none",
              }}
              icon={<ArrowDownOutlined />}
            >
              Bottom 10
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Loading State */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        /* Data Cards */
        data.map((item) => {
          const sampleInfo = getSampleInfo(item, activeTab);
          const barColor = getColor(Number(sampleInfo.percentage));
          const bgColor = getBgColor(Number(sampleInfo.percentage));
          const percent = isNaN(Number(sampleInfo.percentage))
            ? null
            : Number(sampleInfo.percentage);

          return (
            <Card
              key={`${item.rank}-${item.commodityName}`}
              bodyStyle={{ padding: "8px 16px" }}
              style={{
                marginBottom: 9,
                borderRadius: 8,
                backgroundColor: bgColor,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Row align="middle" justify="space-between">
                <Col span={22}>
                  <h3 style={{ marginBottom: 4 }}>
                    {item.rank}. {item.commodityName}
                  </h3>
                  <div
                    className="sample-info"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "75%",
                      margin: "0 auto",
                    }}
                  >
                    <div
                      style={{
                        textAlign: "center",
                        flex: 1,
                      }}
                    >
                      <div style={{ color: "#475569" }}>
                        {sampleInfo.label1}
                      </div>
                      <div style={{ fontWeight: 600 }}>{sampleInfo.value1}</div>
                    </div>

                    <div
                      style={{
                        textAlign: "center",
                        flex: 1,
                        marginRight: "10%",
                      }}
                    >
                      <div style={{ color: "#475569" }}>
                        {sampleInfo.label2}
                      </div>
                      <div style={{ color: barColor, fontWeight: 600 }}>
                        {sampleInfo.value2}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col span={2} style={{ textAlign: "right" }}>
                  <h2 style={{ color: barColor, margin: 0 }}>
                    {isNaN(Number(sampleInfo.percentage))
                      ? "N/A"
                      : `${sampleInfo.percentage}%`}
                  </h2>
                </Col>
              </Row>

              <div className="progress-container">
                {percent === null ? (
                  <div
                    className="progress-fill"
                    style={{
                      width: "100%",
                      backgroundColor: "transparent",
                      color: "#999",
                      justifyContent: "center",
                      padding: "8px 0",
                      border: "1px dashed #ccc",
                    }}
                  >
                    <span className="progress-label">N/A</span>
                  </div>
                ) : (
                  <div
                    className="progress-fill"
                    style={{
                      width: `${percent}%`,
                      backgroundColor: barColor,
                      justifyContent: percent < 15 ? "flex-start" : "flex-end",
                      paddingLeft: percent < 15 ? "8px" : "0",
                      paddingRight: percent >= 15 ? "8px" : "0",
                    }}
                  >
                    <span className="progress-label">{sampleInfo.value2}</span>
                  </div>
                )}
              </div>
            </Card>
          );
        })
      )}
    </Card>
  );
};

export default CommodityCompliance;
